import { ref, computed } from "vue";

/**
 * `Branch` is an array of indexes, pointing to the current position of a node relative to the root node.
 *
 * ---
 *
 * The backend/legacy structure only uses a `label` and `level` as identifier for a node,
 * which requires up to a full tree navigation to find the node.
 * This will have to be done again if we want to fetch the node's parent or children
 *
 * ---
 *
 * Storing an array of indexes means we already know where to navigate to.
 * It also makes navigating to a node's parent or children much easier:
 * - To parent -> `branch.pop()`
 * - To child -> `branch.push(childIndex)`
 *
 * ```
 * All Carparks
 * ├── Personeelsparkeren   [0]
 * │   ├── P12                [0, 0]
 * │   ├── P22                [0, 1]
 * │   └── ...
 * └── Publieksparkeren     [1]
 *     ├── Centrum            [1, 0]
 *     │   ├── Holiday Valet    [1, 0, 0]
 *     │   ├── P1               [1, 0, 1]
 *     │   └── ...
 *     └── Remote             [1, 1]
 *         └── ...
 * ```
 */
export type Branch = number[];

export function useExcludedBranch() {
	/**
	 * A `JSON.stringify()`ed set of the branches, use `convert` when adding branches
	 */
	const excludedBranchesMap = ref<Set<string>>(new Set());
	const excludedBranchesArray = computed(() =>
		Array.from(excludedBranchesMap.value)
	);

	const excludedBranches = computed((): Branch[] =>
		Array.from(excludedBranchesMap.value).map((eb) => JSON.parse(eb))
	);

	const convert = (branch: Branch) => JSON.stringify(branch);

	const deleteBranch = (branch: Branch) => {
		excludedBranchesMap.value.delete(convert(branch));
	};

	const deleteBranchAndChildren = (branch: Branch) => {
		/**
		 * "]" needs to be replaced with ",", otherwise siblings of the deleted branch may be deleted:
		 * - deleted node -> "[3, 1]"
		 * - match to delete (without ",") -> "[3, 1"
		 * - child (deleted) -> "[3, 1, 2]"
		 * - sibling (also deleted) -> "[3, 11]"
		 */
		const branchStart = convert(branch).replace("]", ",");

		deleteBranch(branch);
		excludedBranchesArray.value
			.filter((branch) => branch.startsWith(branchStart))
			.forEach((branch) => excludedBranchesMap.value.delete(branch));
	};

	const toggleBranch = (branch: Branch) => {
		const converted = convert(branch);

		if (excludedBranchesMap.value.has(converted)) {
			deleteBranchAndChildren(branch);
		} else {
			excludedBranchesMap.value.add(converted);
		}
	};

	const setBranch = (branch: Branch, value: boolean, recursive = false) => {
		const converted = convert(branch);

		if (value) {
			excludedBranchesMap.value.add(converted);
		} else {
			/**
			 * Generally, whatever state the child nodes are in is kept the same when toggling
			 * Except, when going from indeterminate -> included
			 */
			if (recursive) {
				deleteBranchAndChildren(branch);
			} else {
				deleteBranch(branch);
			}
		}
	};

	const isBranchExcluded = (branch: Branch) =>
		excludedBranchesMap.value.has(convert(branch));

	const isBranchIndeterminate = (branch: Branch) => {
		if (isBranchExcluded(branch)) return false;

		const branchStart = convert(branch).replace("]", ",");

		return excludedBranchesArray.value.some((branch: string) =>
			branch.startsWith(branchStart)
		);
	};

	const clearBranches = () => excludedBranchesMap.value.clear();

	return {
		excludedBranches,
		toggleBranch,
		setBranch,
		isBranchExcluded,
		isBranchIndeterminate,
		clearBranches,
	};
}
